import type { KeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../i18n/index.js';

export interface MultiSelectOption {
  value: string;
  /** Qisqa belgi — kod. Monoshirift bilan, chapda turadi. */
  code?: string;
  label: string;
}

export interface MultiSelectProps {
  label?: ReactNode;
  /** Ko'rinmas yorliq — jadval boshi ustunni allaqachon nomlagan joylarda. */
  hiddenLabel?: boolean;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** Hech narsa tanlanmaganda ko'rinadigan matn. */
  placeholder?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

/** Qidiruv maydoni shundan ko'p variantda chiziladi. */
const SEARCH_FROM = 8;

const matches = (option: MultiSelectOption, query: string): boolean => {
  if (query === '') return true;
  const needle = query.toLocaleLowerCase();
  return (
    option.label.toLocaleLowerCase().includes(needle) ||
    (option.code ?? '').toLocaleLowerCase().includes(needle)
  );
};

/**
 * BIR NECHTA TANLASH.
 *
 * `<select multiple>` ATAYLAB ISHLATILMADI. U brauzerda bir necha qatorli
 * quti bo'lib chiqadi va tanlash uchun `Ctrl` ni bosib turish kerak —
 * qo'lqopdagi omborchi buni bilmaydi ham, qila olmaydi ham. Bundan
 * yomoni: tanlangandan keyin yana bittasini oddiy bosish bilan tanlasa,
 * oldingilari JIMGINA bekor bo'ladi.
 *
 * Ro'yxat SUZMAYDI, ochilganda joyni surib ochiladi. Bu komponent modal
 * oyna ichida ishlaydi va oynaning tanasi siljiydigan (`overflow-y-auto`)
 * qutida turadi: suzuvchi ro'yxat o'sha qutining chetida kesilib qolardi.
 * Surib ochilgan ro'yxat esa oynaning o'z siljishiga qo'shilib ketadi.
 *
 * Tanlanganlar TEPADA, belgilar bo'lib turadi — ro'yxat yopilganda ham
 * "nima tanlangan" degan savolga javob ekranda qoladi.
 */
export const MultiSelect = ({
  label,
  hiddenLabel = false,
  options,
  value,
  onChange,
  placeholder,
  hint,
  error,
  disabled = false,
  className,
}: MultiSelectProps) => {
  const autoId = useId();
  const listId = `${autoId}-list`;
  const errorId = `${autoId}-error`;
  const hintId = `${autoId}-hint`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  /* Ro'yxat ochilganda fokus darhol qidiruvga tushadi: variant ko'p
     bo'lgan joyda birinchi harakat baribir terish bo'ladi. */
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  const chosen = useMemo(() => new Set(value), [value]);
  const visible = useMemo(
    () => options.filter((option) => matches(option, query)),
    [options, query],
  );
  const picked = useMemo(
    () => options.filter((option) => chosen.has(option.value)),
    [options, chosen],
  );

  const toggle = (option: string): void =>
    onChange(chosen.has(option) ? value.filter((id) => id !== option) : [...value, option]);

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setOpen(false);
      return;
    }
    // Terib bo'lib `Enter` bosish — birinchi mos variantni tanlaydi.
    const first = visible[0];
    if (event.key === 'Enter' && first !== undefined) {
      event.preventDefault();
      toggle(first.value);
    }
  };

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? (
        <span
          id={`${autoId}-label`}
          className={cn('text-text text-sm font-medium', hiddenLabel ? 'sr-only' : undefined)}
        >
          {label}
        </span>
      ) : null}

      <div
        className={cn(
          'rounded-[var(--radius-sm)] border bg-surface',
          'transition-[border-color] duration-[var(--motion-fast)]',
          error ? 'border-danger' : 'border-border-control',
          disabled ? 'opacity-60' : undefined,
        )}
      >
        <button
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          {...(label ? { 'aria-labelledby': `${autoId}-label` } : {})}
          onClick={() => setOpen((current) => !current)}
          className={cn(
            'flex w-full items-center gap-2 px-3 py-2 text-left',
            'min-h-[var(--size-control)] rounded-[var(--radius-sm)]',
            'disabled:cursor-not-allowed',
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {picked.length === 0 ? (
              <span className="text-text-faint text-base">
                {placeholder ?? t('form.multiPlaceholder')}
              </span>
            ) : (
              picked.map((option) => (
                <span
                  key={option.value}
                  className={cn(
                    'bg-accent-soft text-accent border-accent/25 flex max-w-full items-center',
                    'gap-1 rounded-[var(--radius-pill)] border py-0.5 pr-1 pl-2 text-xs',
                  )}
                >
                  {option.code === undefined ? null : (
                    <span className="tabular shrink-0 text-[0.6875rem] opacity-80">
                      {option.code}
                    </span>
                  )}
                  <span className="truncate">{option.label}</span>
                  {/*
                    Ichma-ich tugma bo'lmaydi — bu `span`, lekin u
                    klaviatura bilan ham yetiladigan nishon: `role` va
                    `tabIndex` bilan. Aks holda belgini olib tashlash
                    faqat sichqoncha bilan mumkin bo'lardi.
                  */}
                  <span
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-label={t('form.multiRemove', { name: option.label })}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggle(option.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      toggle(option.value);
                    }}
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-full',
                      'hover:bg-accent hover:text-accent-contrast',
                      'transition-colors duration-[var(--motion-fast)]',
                    )}
                  >
                    <X size={10} aria-hidden />
                  </span>
                </span>
              ))
            )}
          </span>

          <ChevronDown
            size={16}
            aria-hidden
            className={cn(
              'text-text-faint shrink-0 transition-transform duration-[var(--motion-fast)]',
              open ? 'rotate-180' : undefined,
            )}
          />
        </button>

        {!open || disabled ? null : (
          <div id={listId} className="border-border border-t">
            {options.length < SEARCH_FROM ? null : (
              <div className="border-border relative border-b">
                <Search
                  size={14}
                  aria-hidden
                  className="text-text-faint pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={t('form.multiSearch')}
                  aria-label={t('form.multiSearch')}
                  className="text-text h-10 w-full bg-transparent pr-3 pl-9 text-base outline-none"
                />
              </div>
            )}

            <ul
              role="listbox"
              aria-multiselectable
              className="max-h-56 overflow-y-auto py-1"
            >
              {visible.length === 0 ? (
                <li className="text-text-faint px-3 py-3 text-sm">{t('form.multiEmpty')}</li>
              ) : (
                visible.map((option) => {
                  const active = chosen.has(option.value);
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => toggle(option.value)}
                        className={cn(
                          'flex w-full items-center gap-2.5 px-3 py-2 text-left',
                          'transition-colors duration-[var(--motion-fast)]',
                          'hover:bg-surface-2',
                          active ? 'bg-accent-soft' : undefined,
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                            active
                              ? 'border-accent bg-accent text-accent-contrast'
                              : 'border-border-control',
                          )}
                        >
                          {active ? <Check size={11} strokeWidth={3} /> : null}
                        </span>
                        {option.code === undefined ? null : (
                          <span className="tabular text-text-faint shrink-0 text-xs">
                            {option.code}
                          </span>
                        )}
                        <span className="text-text min-w-0 flex-1 truncate text-sm">
                          {option.label}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="border-border bg-surface-2 flex items-center justify-between gap-2 border-t px-3 py-2">
              <span className="text-text-muted text-xs tabular-nums">
                {t('form.multiCount', { count: picked.length })}
              </span>
              <button
                type="button"
                disabled={picked.length === 0}
                onClick={() => onChange([])}
                className={cn(
                  'text-text-muted hover:text-danger text-xs',
                  'transition-colors duration-[var(--motion-fast)]',
                  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-text-muted',
                )}
              >
                {t('form.clearSelection')}
              </button>
            </div>
          </div>
        )}
      </div>

      {error ? (
        <p id={errorId} className="text-danger text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-text-faint text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
